import { TextContentElement } from '@deltek/specification-client/core/Models/textContentElement'
import { useCallback, useLayoutEffect } from 'react'
import { usePromoteDemoteSelector } from '../PromoteDemoteStore'
import { ContentItemViewNodesType } from '../types'
import { tryGetShortcutKey } from '../utils/shortcutKeyUtils'
import { useShallowSelector } from '../Workspace2Store'

/**
 * Hook that encapsulates promote/demote functionality for text content elements.
 *
 * @returns An object containing state and functions for promote/demote operations
 */
export const usePromoteDemote = () => {
    const {
        isContentTeamFromStudioPage,
        isEditingPlainText,
    } = useShallowSelector((s) => ({
        isContentTeamFromStudioPage: s.isContentTeamFromStudioPage,
        isEditingPlainText: s.isEditingPlainText,
    }))

    const {
        canBePromoted,
        canBeDemoted,
        contentItemToPromoteDemote: activeItem,
        setContentItemToPromoteDemote,
        resetPromoteDemoteState: resetPromoteDemote,
    } = usePromoteDemoteSelector((s) => ({
        canBePromoted: s.canBePromoted,
        canBeDemoted: s.canBeDemoted,
        contentItemToPromoteDemote: s.contentItemToPromoteDemote,
        setContentItemToPromoteDemote: s.setContentItemToPromoteDemote,
        resetPromoteDemoteState: s.resetPromoteDemoteState,
    }))

    const canPromote = canBePromoted(isContentTeamFromStudioPage)
    const canDemote = canBeDemoted(isContentTeamFromStudioPage)

    // Register content for promote/demote operations
    const register = useCallback(
        (
            contentItem: Partial<TextContentElement>,
            isInProject: boolean = false,
            contentItemViewNodes: Partial<ContentItemViewNodesType>[] = []
        ) => {
            setContentItemToPromoteDemote({
                contentItemToPromoteDemote: contentItem,
                isInProject,
                contentItemViewNodes,
            })
        },
        []
    )

    // Handle keyboard events for promote/demote shortcuts
    const handlePromoteDemoteShortcutKey = useCallback(
        (event: React.KeyboardEvent<HTMLElement>) => {
            if (tryGetShortcutKey('Promote', event) && canPromote) {
                // TODO: implement promote mutation
            } else if (tryGetShortcutKey('Demote', event) && canDemote) {
                // TODO: implement demote mutation
            }
        },
        [canPromote, canDemote]
    )

    /**
     * side-effect: reset promote/demote state when nothing is selected
     * Toggles the Promote/Demote buttons on action bar
     */
    useLayoutEffect(() => {
        let resetId: NodeJS.Timeout

        if (!isEditingPlainText && activeItem) {
            resetId = setTimeout(() => {
                // reset means toggle the promote/demote buttons on action bar
                resetPromoteDemote()
            }, 100) // 100ms delay to ensure that the current item is evaluated by action bar before resetting the state
        }

        return () => clearTimeout(resetId)
    }, [isEditingPlainText, activeItem, resetPromoteDemote])

    return {
        canPromote,
        canDemote,
        activeItem,
        register,
        handlePromoteDemoteShortcutKey,
        resetPromoteDemote,
    }
}
